import { z } from 'zod';
import { hvacStateSchema, powertrainTypeSchema, routeTypeSchema, runSourceSchema, runStatusSchema, seasonSchema, testTypeSchema, timeBandSchema, trafficLevelSchema, validationOutcomeSchema, } from '../../common/schemas/enum.schemas.js';
import { idParamSchema, uuidSchema } from '../../common/schemas/id.schemas.js';
import { paginatedSchema, paginationInputSchema, sortInputSchema } from '../../common/schemas/pagination.schemas.js';
/** `test_date` is a calendar date, never a timestamp — a run belongs to a test day, not an instant. */
export const runDateSchema = z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a YYYY-MM-DD date');
/** Postgres `time` reads back as `HH:MM:SS`; the wizard sends `HH:MM`, so both are accepted. */
export const clockTimeSchema = z
    .string()
    .trim()
    .regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, 'Use a HH:MM time');
/** Physical envelopes wide enough for any real test; they only exist to reject typos and bad imports. */
export const MIN_TEMP_C = -40;
export const MAX_TEMP_C = 70;
export const MIN_PRESSURE_KPA = 100;
export const MAX_PRESSURE_KPA = 500;
export const MAX_WEIGHT_KG = 5000;
export const MAX_DISTANCE_KM = 10_000;
export const MAX_DURATION_SEC = 1_000_000;
export const MAX_FUEL_L = 10_000;
export const MAX_RPM = 12_000;
export const MAX_SPEED_KPH = 400;
const tempC = z.number().min(MIN_TEMP_C).max(MAX_TEMP_C);
const pressureKpa = z.number().min(MIN_PRESSURE_KPA).max(MAX_PRESSURE_KPA);
const percent = z.number().min(0).max(100);
const weightKg = z.number().min(0).max(MAX_WEIGHT_KG);
const speedKph = z.number().min(0).max(MAX_SPEED_KPH);
const wholeSeconds = z.number().int().min(0).max(MAX_DURATION_SEC);
/**
 * The pre-test checklist (plan §4 `run_conditions`). Everything is nullable: the validity engine — not
 * the column definitions — decides which fields a protocol actually requires, so a new sensor never
 * needs a migration.
 */
export const runConditionsSchema = z.object({
    driveMode: z.string().nullable(),
    /** How busy the road was on the day — a property of the test, not of the route. */
    trafficLevel: trafficLevelSchema.nullable(),
    odometerKm: z.number().int().nullable(),
    warningLightsPresent: z.boolean().nullable(),
    tireMake: z.string().nullable(),
    tireModel: z.string().nullable(),
    pressureFlKpa: z.number().nullable(),
    pressureFrKpa: z.number().nullable(),
    pressureRlKpa: z.number().nullable(),
    pressureRrKpa: z.number().nullable(),
    driverWeightKg: z.number().nullable(),
    passengerCount: z.number().int().nullable(),
    passengerWeightKg: z.number().nullable(),
    ballastWeightKg: z.number().nullable(),
    totalPayloadKg: z.number().nullable(),
    fuelGrade: z.string().nullable(),
    fuelSource: z.string().nullable(),
    startingFuelCondition: z.string().nullable(),
    fuelMeasurementMethod: z.string().nullable(),
    hvacSetpointC: z.number().nullable(),
    fanLevel: z.number().int().nullable(),
    recirculation: z.boolean().nullable(),
    windowsState: z.string().nullable(),
    engineCoolantTempC: z.number().nullable(),
    cabinTempC: z.number().nullable(),
    hybridSocStartPct: z.number().nullable(),
    soakDurationMin: z.number().int().nullable(),
    updatedAt: z.string().datetime(),
});
/** During/after the run (plan §4 `run_measurements`). `extra` catches sensors we have no column for. */
export const runMeasurementsSchema = z.object({
    ambientTempStartC: z.number().nullable(),
    ambientTempEndC: z.number().nullable(),
    ambientTempAvgC: z.number().nullable(),
    relativeHumidityAvgPct: z.number().nullable(),
    windSpeedAvgKph: z.number().nullable(),
    roadSurface: z.string().nullable(),
    precipitation: z.string().nullable(),
    distanceTotalKm: z.number().nullable(),
    durationTotalSec: z.number().int().nullable(),
    movingTimeSec: z.number().int().nullable(),
    stationaryPct: z.number().nullable(),
    avgSpeedKph: z.number().nullable(),
    maxSpeedKph: z.number().nullable(),
    stopCount: z.number().int().nullable(),
    elevationGainM: z.number().int().nullable(),
    elevationLossM: z.number().int().nullable(),
    fuelConsumedL: z.number().nullable(),
    engineOffTimeSec: z.number().int().nullable(),
    engineRpmAvg: z.number().int().nullable(),
    hybridSocEndPct: z.number().nullable(),
    evTimeSec: z.number().int().nullable(),
    evDistanceKm: z.number().nullable(),
    extra: z.record(z.unknown()).nullable(),
    updatedAt: z.string().datetime(),
});
export const testResultSchema = z.object({
    id: uuidSchema,
    metric: z.string().min(1),
    value: z.number(),
    unit: z.string().min(1),
    calculationVersion: z.string().min(1),
    createdAt: z.string().datetime(),
});
export const runValidationSchema = z.object({
    id: uuidSchema,
    ruleKey: z.string().min(1),
    outcome: validationOutcomeSchema,
    actualValue: z.string().nullable(),
    expected: z.string().nullable(),
    /** Why the rule landed where it did — the missing-field reason, or the manual-override note. */
    message: z.string().nullable(),
    evaluatedAt: z.string().datetime(),
});
const runVehicleSchema = z.object({
    id: uuidSchema,
    code: z.string().min(1),
    label: z.string().min(1),
    powertrainType: powertrainTypeSchema,
});
const runScenarioSchema = z.object({
    id: uuidSchema,
    code: z.string().min(1),
    testType: testTypeSchema,
    season: seasonSchema,
    timeBand: timeBandSchema,
    hvacState: hvacStateSchema,
    routeType: routeTypeSchema.nullable(),
    idleDurationMin: z.number().int().nullable(),
    /** The traffic the scenario *expects*; what this run actually met is `conditions.trafficLevel`. */
    trafficLevel: trafficLevelSchema.nullable(),
});
const runRouteSchema = z.object({
    id: uuidSchema,
    code: z.string().min(1),
    name: z.string().min(1),
    routeType: routeTypeSchema,
    version: z.number().int().min(1),
    /** `null` when the route was never surveyed — the `distance_completed` rule then does not apply. */
    distanceKm: z.number().nullable(),
    /** `↑ 210 m over 9 km (2.3%) · ↓ 210 m over 9 km (−2.3%)`, or `null` when the route has no survey. */
    elevationProfile: z.string().nullable(),
});
const runProtocolSchema = z.object({
    id: uuidSchema,
    code: z.string().min(1),
    version: z.number().int().min(1),
    metric: z.string().min(1),
    unit: z.string().min(1),
});
/** One row of the runs list: everything the table renders, joined in a single query. */
export const runSummarySchema = z.object({
    id: uuidSchema,
    testDate: z.string(),
    startTime: z.string().nullable(),
    endTime: z.string().nullable(),
    status: runStatusSchema,
    source: runSourceSchema,
    campaignTag: z.string().nullable(),
    driverName: z.string().nullable(),
    operatorName: z.string().nullable(),
    vehicle: runVehicleSchema,
    scenario: runScenarioSchema,
    route: runRouteSchema.nullable(),
    protocol: runProtocolSchema,
    /** The computed figure — `null` until the run is submitted. */
    result: testResultSchema.nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const runDetailSchema = runSummarySchema.extend({
    notes: z.string().nullable(),
    conditions: runConditionsSchema.nullable(),
    measurements: runMeasurementsSchema.nullable(),
    results: z.array(testResultSchema),
    validations: z.array(runValidationSchema),
});
export const RUN_SORT_FIELDS = ['testDate', 'createdAt', 'status'];
export const runSortBySchema = z.enum(RUN_SORT_FIELDS);
export const listRunsInputSchema = z.object({
    ...paginationInputSchema.shape,
    ...sortInputSchema.shape,
    sortBy: runSortBySchema.default('testDate'),
    sortDirection: z.enum(['asc', 'desc']).default('desc'),
    vehicleId: uuidSchema.optional(),
    scenarioId: uuidSchema.optional(),
    status: runStatusSchema.optional(),
    season: seasonSchema.optional(),
    source: runSourceSchema.optional(),
    /** Narrows the list to the runs one spreadsheet import created — the link target of the import success panel. */
    importBatchId: uuidSchema.optional(),
});
export const listRunsOutputSchema = paginatedSchema(runSummarySchema);
/**
 * A draft is the run's identity: vehicle, scenario, route and date. The scenario decides whether a
 * route is required at all (IDLE runs have none), and the service re-checks that against the route's
 * type — the wizard's filtering is convenience, not enforcement.
 */
export const createRunDraftInputSchema = z.object({
    vehicleId: uuidSchema,
    scenarioId: uuidSchema,
    routeId: uuidSchema.optional(),
    testDate: runDateSchema,
    startTime: clockTimeSchema.optional(),
    endTime: clockTimeSchema.optional(),
    driverName: z.string().trim().max(128).optional(),
    operatorName: z.string().trim().max(128).optional(),
    campaignTag: z.string().trim().max(64).optional(),
    notes: z.string().trim().max(2000).optional(),
});
/**
 * `undefined` leaves a field alone, `null` clears it — the wizard PATCHes the same draft repeatedly as
 * the engineer fills the checklist in, so a partially-filled form must not wipe earlier answers.
 */
export const runConditionsFormSchema = z.object({
    driveMode: z.string().trim().max(32).nullish(),
    trafficLevel: trafficLevelSchema.nullish(),
    odometerKm: z.number().int().min(0).max(10_000_000).nullish(),
    warningLightsPresent: z.boolean().nullish(),
    tireMake: z.string().trim().max(64).nullish(),
    tireModel: z.string().trim().max(64).nullish(),
    pressureFlKpa: pressureKpa.nullish(),
    pressureFrKpa: pressureKpa.nullish(),
    pressureRlKpa: pressureKpa.nullish(),
    pressureRrKpa: pressureKpa.nullish(),
    driverWeightKg: weightKg.nullish(),
    passengerCount: z.number().int().min(0).max(12).nullish(),
    passengerWeightKg: weightKg.nullish(),
    ballastWeightKg: weightKg.nullish(),
    totalPayloadKg: weightKg.nullish(),
    fuelGrade: z.string().trim().max(32).nullish(),
    fuelSource: z.string().trim().max(128).nullish(),
    startingFuelCondition: z.string().trim().max(64).nullish(),
    fuelMeasurementMethod: z.string().trim().max(64).nullish(),
    hvacSetpointC: tempC.nullish(),
    fanLevel: z.number().int().min(0).max(10).nullish(),
    recirculation: z.boolean().nullish(),
    windowsState: z.string().trim().max(32).nullish(),
    engineCoolantTempC: tempC.nullish(),
    cabinTempC: tempC.nullish(),
    hybridSocStartPct: percent.nullish(),
    soakDurationMin: z.number().int().min(0).max(10_000).nullish(),
});
export const updateRunConditionsInputSchema = z.object({
    ...idParamSchema.shape,
    ...runConditionsFormSchema.shape,
});
export const runMeasurementsFormSchema = z.object({
    ambientTempStartC: tempC.nullish(),
    ambientTempEndC: tempC.nullish(),
    ambientTempAvgC: tempC.nullish(),
    relativeHumidityAvgPct: percent.nullish(),
    windSpeedAvgKph: speedKph.nullish(),
    roadSurface: z.string().trim().max(32).nullish(),
    precipitation: z.string().trim().max(32).nullish(),
    distanceTotalKm: z.number().min(0).max(MAX_DISTANCE_KM).nullish(),
    durationTotalSec: wholeSeconds.nullish(),
    movingTimeSec: wholeSeconds.nullish(),
    stationaryPct: percent.nullish(),
    avgSpeedKph: speedKph.nullish(),
    maxSpeedKph: speedKph.nullish(),
    stopCount: z.number().int().min(0).max(100_000).nullish(),
    elevationGainM: z.number().int().min(0).max(10_000).nullish(),
    elevationLossM: z.number().int().min(0).max(10_000).nullish(),
    fuelConsumedL: z.number().min(0).max(MAX_FUEL_L).nullish(),
    engineOffTimeSec: wholeSeconds.nullish(),
    engineRpmAvg: z.number().int().min(0).max(MAX_RPM).nullish(),
    hybridSocEndPct: percent.nullish(),
    evTimeSec: wholeSeconds.nullish(),
    evDistanceKm: z.number().min(0).max(MAX_DISTANCE_KM).nullish(),
    extra: z.record(z.unknown()).nullish(),
});
export const updateRunMeasurementsInputSchema = z.object({
    ...idParamSchema.shape,
    ...runMeasurementsFormSchema.shape,
});
export const invalidateRunInputSchema = z.object({
    ...idParamSchema.shape,
    note: z.string().trim().min(1, 'Say why this run is being invalidated').max(1000),
});
//# sourceMappingURL=runs.schemas.js.map